import { z } from 'zod';

/**
 * One row in core.time_entries.  Either a recorded Start/Stop interval or a
 * manual hours log.  `endedAt`/`durationSeconds` are nullable only while a
 * timer is still running (source='timer').
 */
export const TimeEntrySchema = z.object({
  id: z.string().uuid(),
  taskId: z.string().uuid(),
  projectId: z.string().uuid(),
  /** Credit recipient — the user whose hours this row counts toward
   *  in the time report.  For self-logged rows equals `loggedByUserId`. */
  userId: z.string().uuid(),
  /** Audit field — who clicked the button.  Differs from `userId`
   *  only for "log on behalf" manual entries that a respondent / lead
   *  enters for the assignee (the executor gets the credit; the
   *  actor stays in `loggedByUserId` for traceability). */
  loggedByUserId: z.string().uuid(),
  source: z.enum(['timer', 'manual']),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  durationSeconds: z.number().int().nonnegative().nullable(),
  note: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TimeEntryProjection = z.infer<typeof TimeEntrySchema>;

/** Start a timer on a task.  Stops any running timer the actor already owns. */
export const StartTimerSchema = z.object({
  note: z.string().max(500).optional(),
});
export type StartTimerInput = z.infer<typeof StartTimerSchema>;

/** Stop the actor's running timer.  Optional note replaces any prior note. */
export const StopTimerSchema = z.object({
  note: z.string().max(500).optional(),
});
export type StopTimerInput = z.infer<typeof StopTimerSchema>;

/**
 * Manual hours entry — durationSeconds is what gets logged; `startedAt` is
 * optional (defaults to "now - durationSeconds") and useful for backdating
 * yesterday's work without a stopwatch.
 *
 * `forUserId` is the on-behalf-of split — set this when a respondent /
 * lead logs hours that should attribute to the assignee.  Requires
 * `time.log_any` on the actor; missing → server uses caller as userId.
 * Omit entirely for self-logged entries.
 */
export const ManualTimeEntrySchema = z.object({
  durationSeconds: z.number().int().positive().max(86_400 * 7),
  startedAt: z.string().datetime().optional(),
  note: z.string().max(500).optional(),
  forUserId: z.string().uuid().optional(),
});
export type ManualTimeEntryInput = z.infer<typeof ManualTimeEntrySchema>;

/** List entries for a task. */
export const TimeEntriesListSchema = z.array(TimeEntrySchema);
export type TimeEntriesList = z.infer<typeof TimeEntriesListSchema>;

/** Currently-running timer for the actor (single, optional). */
export const ActiveTimerSchema = z.object({
  entry: TimeEntrySchema.nullable(),
  /** Task title + key for header display — fetched serverside so the UI doesn't need a second call. */
  task: z
    .object({
      id: z.string().uuid(),
      key: z.string(),
      title: z.string(),
      projectId: z.string().uuid(),
    })
    .nullable(),
});
export type ActiveTimerProjection = z.infer<typeof ActiveTimerSchema>;

// ── Personal time report (My Time desktop page) ────────────────────────────

/**
 * Personal time roll-up over a window [from, to].  Drives the desktop
 * `/my/time` page — replaces the Phase-1 fixture data with real
 * aggregation.  Mobile uses the same shape for parity.
 *
 * Buckets are MY local days: `?tz=<IANA zone>` (the web sends the device
 * zone), else `users.timezone` — the window runs midnight to midnight there.
 */
const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const MeTimeDailySchema = z.object({
  date: DateOnly,
  timerSec: z.number().int().nonnegative(),
  manualSec: z.number().int().nonnegative(),
  entries: z.number().int().nonnegative(),
});
export type MeTimeDailyRow = z.infer<typeof MeTimeDailySchema>;

export const MeTimeByProjectSchema = z.object({
  projectId: z.string().uuid(),
  projectName: z.string(),
  projectKey: z.string(),
  projectColor: z.string().nullable(),
  teamSlug: z.string(),
  projectSlug: z.string(),
  totalSec: z.number().int().nonnegative(),
  timerSec: z.number().int().nonnegative(),
  manualSec: z.number().int().nonnegative(),
  entries: z.number().int().nonnegative(),
});
export type MeTimeByProjectRow = z.infer<typeof MeTimeByProjectSchema>;

export const MeTimeTopTaskSchema = z.object({
  taskId: z.string().uuid(),
  taskKey: z.string(),
  title: z.string(),
  totalSec: z.number().int().nonnegative(),
});
export type MeTimeTopTaskRow = z.infer<typeof MeTimeTopTaskSchema>;

export const MeTimeRangeSchema = z.object({
  range: z.object({
    from: DateOnly,
    to: DateOnly,
  }),
  daily: z.array(MeTimeDailySchema),
  byProject: z.array(MeTimeByProjectSchema),
  topTasks: z.array(MeTimeTopTaskSchema),
  totals: z.object({
    totalSec: z.number().int().nonnegative(),
    timerSec: z.number().int().nonnegative(),
    manualSec: z.number().int().nonnegative(),
    entries: z.number().int().nonnegative(),
    /** Distinct local days (`tz`, else the profile zone) the actor logged anything in the window. */
    activeDays: z.number().int().nonnegative(),
    /** Total / activeDays, rounded to whole seconds (0 when no days). */
    avgSecPerActiveDay: z.number().int().nonnegative(),
  }),
  /** Total for the immediately-preceding window of equal length — drives
   *  the "vs last period" delta indicator. */
  previousTotalSec: z.number().int().nonnegative(),
});
export type MeTimeRangeProjection = z.infer<typeof MeTimeRangeSchema>;

// ── Team time report (manager view) ────────────────────────────────────────

/**
 * Team-scoped time report.  Same shape as MeTimeRange but with a
 * `byUser` slice that aggregates across the whole team, gated by
 * `time.view_team`.  Lets a LEAD/MANAGER/OWNER see who's logging how
 * much per period.
 */
export const TeamTimeUserSchema = z.object({
  userId: z.string().uuid(),
  name: z.string(),
  username: z.string(),
  avatarUrl: z.string().nullable(),
  totalSec: z.number().int().nonnegative(),
  timerSec: z.number().int().nonnegative(),
  manualSec: z.number().int().nonnegative(),
  entries: z.number().int().nonnegative(),
  activeDays: z.number().int().nonnegative(),
});
export type TeamTimeUserRow = z.infer<typeof TeamTimeUserSchema>;

export const TeamTimeReportSchema = z.object({
  range: z.object({ from: DateOnly, to: DateOnly }),
  daily: z.array(MeTimeDailySchema),
  byUser: z.array(TeamTimeUserSchema),
  byProject: z.array(MeTimeByProjectSchema),
  topTasks: z.array(MeTimeTopTaskSchema),
  totals: z.object({
    totalSec: z.number().int().nonnegative(),
    timerSec: z.number().int().nonnegative(),
    manualSec: z.number().int().nonnegative(),
    entries: z.number().int().nonnegative(),
    contributors: z.number().int().nonnegative(),
  }),
  previousTotalSec: z.number().int().nonnegative(),
});
export type TeamTimeReportProjection = z.infer<typeof TeamTimeReportSchema>;
